import React, { useState, useCallback, useEffect } from 'react';
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import Input from "@amzn/meridian/input";
import Button from "@amzn/meridian/button";
import Table, { TableRow, TableCell } from "@amzn/meridian/table";
import Checkbox from "@amzn/meridian/checkbox";
import Select, { SelectOption } from "@amzn/meridian/select";
import DatePicker from "@amzn/meridian/date-picker";
import Expander from "@amzn/meridian/expander";
import Alert from "@amzn/meridian/alert";
import Loader from "@amzn/meridian/loader";
import { FormattedDate, FormattedMessage, injectIntl } from "react-intl";

import ContainerView from "./ContainerView";
import {WTN_MSG} from "../../commons/constants/WtnMessageIds";
import {FILTER_TYPE, QUERY_TYPE, QUERY_FIELD, SORT_DIRECTION} from "../../commons/constants/WadmQueryFilter";
import {searchEWasteItems} from "../../commons/api/SearchEWasteItems";
import {
    getBatchWasteTransferNotePreSignedUrls,
    WTN_PRESIGNED_URL_BATCH_SIZE
} from "../../commons/api/GetBatchWasteTransferNotePreSignedUrls";
import {getStationName, localStartOfDayIso, localEndOfDayIso} from "../../util/CommonUtils";

const PAGE_SIZE_OPTIONS = [25, 50, 100];
const DEFAULT_PAGE_SIZE = 25;

// Stagger multi-file downloads slightly so browsers don't drop concurrent ones.
const DOWNLOAD_STAGGER_MS = 300;

// Items are returned newest-first unless the user changes sort.
const DEFAULT_SORT_INFOS = [{field: QUERY_FIELD.CREATED_AT, direction: SORT_DIRECTION.DESC}];

/** Splits an array into chunks of at most `size`. */
function chunk(array, size) {
    const chunks = [];
    for (let i = 0; i < array.length; i += size) {
        chunks.push(array.slice(i, i + size));
    }
    return chunks;
}

/**
 * Triggers a browser download for a presigned URL via a transient anchor.
 * WTN presigned URLs are served with Content-Disposition: attachment.
 */
function triggerDownload(url) {
    const link = document.createElement("a");
    link.href = url;
    link.rel = "noopener";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

// Maps each table column to its EWasteSearchItemEntity field. `isDate` columns
// hold ISO timestamps and are rendered via <FormattedDate>.
// Exported (named) for unit testing; the component still uses it internally and the
// default export is unchanged.
export const WTN_TABLE_COLUMNS = [
    { key: "documentId",         messageId: WTN_MSG.TABLE.DOCUMENT_ID,         defaultMessage: "Document ID",         accessor: (item) => item.wtnDocumentId },
    { key: "wasteCode",          messageId: WTN_MSG.TABLE.WASTE_CODE,          defaultMessage: "Waste Code",          accessor: (item) => item.ewcCode || item.wasteProfileId },
    { key: "customerPickupDate", messageId: WTN_MSG.TABLE.CUSTOMER_PICKUP_DATE, defaultMessage: "Customer Pickup Date", accessor: (item) => item.customerPickupDate, isDate: true },
    { key: "itemCreateDate",     messageId: WTN_MSG.TABLE.ITEM_CREATE_DATE,    defaultMessage: "Item Create Date",    accessor: (item) => item.createdAt, isDate: true },
    { key: "itemName",           messageId: WTN_MSG.TABLE.ITEM_NAME,           defaultMessage: "Item Name",           accessor: (item) => item.itemName },
    { key: "containerId",        messageId: WTN_MSG.TABLE.CONTAINER_ID,        defaultMessage: "Container ID",        accessor: (item) => item.containerDisplayId || item.containerId },
    { key: "customerOrderId",    messageId: WTN_MSG.TABLE.CUSTOMER_ORDER_ID,   defaultMessage: "Customer Order ID",   accessor: (item) => item.orderNumber }
];

const INITIAL_FILTERS = {
    itemCreateDateStart: "",
    itemCreateDateEnd: "",
    wasteCode: "",
    documentId: "",
    itemName: "",
    containerId: "",
    customerOrderId: ""
};

/**
 * Builds the WADM-compatible queryFilters array from the WTN dashboard filter
 * state. Most text filters use MATCH (exact, whole-value); itemName uses CONTAINS
 * so operators can find an item by a partial, case-insensitive name (UAT #4).
 * The item-create-date range maps to createdAt GREATER_THAN_EQUAL /
 * LESS_THAN_EQUAL (local-day boundaries). All filtering is server-side — the
 * SearchEWasteItems siteId path runs these against the item index and paginates
 * the result with from/size.
 *
 * itemName is a plain OpenSearch keyword with no normaliser, so exact MATCH only
 * matched a full item name ("Washing Machine") and dropped partials ("Washing").
 * CONTAINS builds a lowercased substring regexp; paired with item names stored
 * lower-cased on the 3P create path (OculusService CreateEWasteItemProvider), a
 * partial query now matches case-insensitively with no index/mapping change or
 * reindex.
 *
 * Exported (named) for unit testing; the component still uses it internally and
 * the default export is unchanged.
 */
export function buildItemQueryFilters(filters) {
    const queryFilters = [];

    const push = (field, rawValue, queryType) => {
        const value = (rawValue || "").trim();
        if (!value) return;
        queryFilters.push({
            filterType: FILTER_TYPE.MUST,
            queryParams: [{field, queryType, value: [value]}]
        });
    };
    const pushMatch = (field, rawValue) => push(field, rawValue, QUERY_TYPE.MATCH);
    const pushContains = (field, rawValue) => push(field, rawValue, QUERY_TYPE.CONTAINS);

    pushMatch(QUERY_FIELD.EWC_CODE, filters.wasteCode);
    pushMatch(QUERY_FIELD.WTN_DOCUMENT_ID, filters.documentId);
    // itemName: partial, case-insensitive search (UAT #4).
    pushContains(QUERY_FIELD.ITEM_NAME, filters.itemName);
    pushMatch(QUERY_FIELD.CONTAINER_ID, filters.containerId);
    pushMatch(QUERY_FIELD.ORDER_NUMBER, filters.customerOrderId);

    if (filters.itemCreateDateStart) {
        queryFilters.push({
            filterType: FILTER_TYPE.MUST,
            queryParams: [{
                field: QUERY_FIELD.CREATED_AT,
                queryType: QUERY_TYPE.GREATER_THAN_EQUAL,
                value: [localStartOfDayIso(filters.itemCreateDateStart)]
            }]
        });
    }

    if (filters.itemCreateDateEnd) {
        queryFilters.push({
            filterType: FILTER_TYPE.MUST,
            queryParams: [{
                field: QUERY_FIELD.CREATED_AT,
                queryType: QUERY_TYPE.LESS_THAN_EQUAL,
                value: [localEndOfDayIso(filters.itemCreateDateEnd)]
            }]
        });
    }

    return queryFilters;
}

const WTNInlineView = ({ intl }) => {
    const [items, setItems] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");

    const [selected, setSelected] = useState({});
    const [downloading, setDownloading] = useState(false);
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [filtersOpen, setFiltersOpen] = useState(true);
    const [pendingFilters, setPendingFilters] = useState(INITIAL_FILTERS);
    const [appliedFilters, setAppliedFilters] = useState(INITIAL_FILTERS);
    const [containerViewOpen, setContainerViewOpen] = useState(false);

    const siteId = getStationName();

    const handleFilterChange = (field, value) => {
        setPendingFilters(prev => ({ ...prev, [field]: value }));
    };

    // Server-side fetch: items for the site, filtered + paginated by WADM.
    useEffect(() => {
        if (!siteId) {
            return undefined;
        }

        let ignore = false;
        setLoading(true);
        setErrorMsg("");

        const queryFilters = buildItemQueryFilters(appliedFilters);
        searchEWasteItems({
            siteId,
            from: currentPage * pageSize,
            size: pageSize,
            queryFilters,
            sortInfos: DEFAULT_SORT_INFOS
        }).then((result) => {
            if (ignore) return;
            if (result.success) {
                setItems(result.entities || []);
                setTotalCount(result.count || 0);
            } else {
                console.error("searchEWasteItems failed", {
                    siteId,
                    from: currentPage * pageSize,
                    size: pageSize,
                    queryFilters,
                    result
                });
                setItems([]);
                setTotalCount(0);
                setErrorMsg(intl.formatMessage({
                    id: WTN_MSG.ERROR.LOAD_FAILED,
                    defaultMessage: "Failed to load documents."
                }));
            }
            setLoading(false);
        });

        return () => { ignore = true; };
    }, [siteId, currentPage, pageSize, appliedFilters, intl]);

    const handleSelectRow = useCallback((item) => {
        setSelected(prev => {
            const next = {...prev};
            if (next[item.id] !== undefined) {
                delete next[item.id];
            } else {
                next[item.id] = item.orderNumber;
            }
            return next;
        });
    }, []);

    const selectedCount = Object.keys(selected).length;
    const totalPages = pageSize > 0 ? Math.ceil(totalCount / pageSize) : 0;
    const allSelected = items.length > 0 && items.every(item => selected[item.id] !== undefined);
    // No active filters → backend applies its default scope (open containers
    // created in the last month). Surface that so the result count makes sense.
    const isDefaultScope = buildItemQueryFilters(appliedFilters).length === 0;

    const handleSelectAll = () => {
        setSelected(prev => {
            const next = {...prev};
            items.forEach(item => {
                if (allSelected) {
                    delete next[item.id];
                } else {
                    next[item.id] = item.orderNumber;
                }
            });
            return next;
        });
    };

    const handleSearch = () => {
        setCurrentPage(0);
        setAppliedFilters(pendingFilters);
        setSelected({});
    };

    const handleClearAll = () => {
        setCurrentPage(0);
        setPendingFilters(INITIAL_FILTERS);
        setAppliedFilters(INITIAL_FILTERS);
        setSelected({});
    };

    const handleDownload = useCallback(async () => {
        // WTN PDFs are keyed by order number; selection is keyed by item id.
        // Dedupe to distinct order numbers and drop items without an order.
        const orderNumbers = [...new Set(Object.values(selected).filter(Boolean))];
        if (orderNumbers.length === 0) {
            return;
        }

        setDownloading(true);
        setErrorMsg("");
        try {
            // The API caps each call at WTN_PRESIGNED_URL_BATCH_SIZE order numbers,
            // so chunk larger selections and merge the resulting URL maps.
            const batches = chunk(orderNumbers, WTN_PRESIGNED_URL_BATCH_SIZE);
            const results = await Promise.all(
                batches.map(batch => getBatchWasteTransferNotePreSignedUrls(batch))
            );

            const urlMap = results.reduce((acc, result) => Object.assign(acc, result.urlMap), {});
            const urls = Object.values(urlMap).filter(Boolean);

            urls.forEach((url, index) => {
                setTimeout(() => triggerDownload(url), index * DOWNLOAD_STAGGER_MS);
            });

            if (results.some(result => !result.success) || urls.length === 0) {
                setErrorMsg(intl.formatMessage({
                    id: WTN_MSG.ERROR.DOWNLOAD_FAILED,
                    defaultMessage: "Failed to download documents."
                }));
            }
        } finally {
            setDownloading(false);
        }
    }, [selected, intl]);

    return (
        <Column spacing="medium" spacingInset="medium none">
            <Row alignmentHorizontal="justify" alignmentVertical="center">
                <Text type="h300">
                    <FormattedMessage id={WTN_MSG.HEADING} defaultMessage="Waste Transfer Note Documents" />
                </Text>
                <Button size="small" type="primary" onClick={() => {
                    setContainerViewOpen(true);
                }}>
                    <FormattedMessage id={WTN_MSG.CONTAINERS} defaultMessage="Containers" />
                </Button>
            </Row>

            {isDefaultScope && (
                <Text type="b200" color="secondary">
                    <FormattedMessage
                        id={WTN_MSG.DEFAULT_SCOPE_HINT}
                        defaultMessage="Showing items in open containers created in the last month. Apply filters to search across all containers."
                    />
                </Text>
            )}

            {/* Filters */}
            <Expander
                type="box"
                open={filtersOpen}
                onChange={setFiltersOpen}
                title={<FormattedMessage id={WTN_MSG.FILTERS} defaultMessage="Filters" />}
            >
                <Column spacingInset="medium">
                        {/* Row 1: Item Create Date, Waste Code, Document ID */}
                        <Row widths={["grid-4", "grid-4", "grid-4"]} spacing="medium" alignmentVertical="bottom">
                            <Column>
                                <Text type="h100"><FormattedMessage id={WTN_MSG.FILTER.ITEM_CREATE_DATE} defaultMessage="Item Create Date" /></Text>
                                <Row spacing="medium">
                                    <DatePicker
                                        value={pendingFilters.itemCreateDateStart}
                                        onChange={(value) => handleFilterChange("itemCreateDateStart", value)}
                                        size="small"
                                    />
                                    <DatePicker
                                        value={pendingFilters.itemCreateDateEnd}
                                        onChange={(value) => handleFilterChange("itemCreateDateEnd", value)}
                                        size="small"
                                    />
                                </Row>
                            </Column>
                            <Column>
                                <Text type="h100"><FormattedMessage id={WTN_MSG.FILTER.WASTE_CODE} defaultMessage="Waste Code" /></Text>
                                <Input
                                    value={pendingFilters.wasteCode}
                                    onChange={(value) => { handleFilterChange("wasteCode", value); }}
                                    size="small"
                                    placeholder="e.g. 16 02 09*"
                                />
                            </Column>
                            <Column>
                                <Text type="h100"><FormattedMessage id={WTN_MSG.FILTER.DOCUMENT_ID} defaultMessage="Document ID" /></Text>
                                <Input
                                    value={pendingFilters.documentId}
                                    onChange={(value) => { handleFilterChange("documentId", value); }}
                                    size="small"
                                    placeholder="e.g. DEEW976596x"
                                />
                            </Column>
                        </Row>
                        {/* Row 2: Customer Order ID, Item Name, Container ID */}
                        <Row widths={["grid-4", "grid-4", "grid-4"]} spacing="medium" alignmentVertical="bottom" spacingInset="small none none none">
                            <Column>
                                <Text type="h100"><FormattedMessage id={WTN_MSG.FILTER.CUSTOMER_ORDER_ID} defaultMessage="Customer Order ID" /></Text>
                                <Input
                                    value={pendingFilters.customerOrderId}
                                    onChange={(value) => { handleFilterChange("customerOrderId", value); }}
                                    size="small"
                                    placeholder="e.g. 123-4567890-1234567"
                                />
                            </Column>
                            <Column>
                                <Text type="h100"><FormattedMessage id={WTN_MSG.FILTER.ITEM_NAME} defaultMessage="Item Name" /></Text>
                                <Input
                                    value={pendingFilters.itemName}
                                    onChange={(value) => { handleFilterChange("itemName", value); }}
                                    size="small"
                                    placeholder="e.g. Samsung Refrigerator"
                                />
                            </Column>
                            <Column>
                                <Text type="h100"><FormattedMessage id={WTN_MSG.FILTER.CONTAINER_ID} defaultMessage="Container ID" /></Text>
                                <Input
                                    value={pendingFilters.containerId}
                                    onChange={(value) => { handleFilterChange("containerId", value); }}
                                    size="small"
                                    placeholder="e.g. HAZLEU-a1b2"
                                />
                            </Column>
                        </Row>
                        {/* Row 3: Search + Clear all */}
                        <Row spacing="medium" spacingInset="small none none none">
                            <Button size="small" onClick={handleSearch}>
                                <FormattedMessage id={WTN_MSG.SEARCH} defaultMessage="Search" />
                            </Button>
                            <Button size="small" type="tertiary" onClick={handleClearAll}>
                                <FormattedMessage id={WTN_MSG.CLEAR_ALL} defaultMessage="Clear all" />
                            </Button>
                        </Row>
                </Column>
            </Expander>

            {/* Actions bar */}
            <Row alignmentHorizontal="justify">
                <Text type="h100">
                    <FormattedMessage
                        id={WTN_MSG.SEARCH_RESULTS}
                        defaultMessage="Search Results ({count})"
                        values={{ count: totalCount }}
                    />
                </Text>
                <Button
                    onClick={handleDownload}
                    disabled={selectedCount === 0 || downloading}
                    size="small"
                >
                    <FormattedMessage id={WTN_MSG.DOWNLOAD} defaultMessage="Download" />
                </Button>
            </Row>

            {errorMsg ? (
                <Alert type="error">{errorMsg}</Alert>
            ) : null}

            {/* Table */}
            <Table headerRows={1} showDividers={true} spacing="small">
                <TableRow>
                    <TableCell>
                        <Checkbox checked={allSelected} onChange={handleSelectAll} disabled={items.length === 0} />
                    </TableCell>
                    {WTN_TABLE_COLUMNS.map((col) => {
                        return (
                            <TableCell key={col.key}>
                                <Text type="h100">
                                    <FormattedMessage id={col.messageId} defaultMessage={col.defaultMessage} />
                                </Text>
                            </TableCell>
                        );
                    })}
                </TableRow>
                {loading ? (
                    <TableRow>
                        <TableCell columnSpan={WTN_TABLE_COLUMNS.length + 1} alignmentHorizontal="center">
                            <Loader />
                        </TableCell>
                    </TableRow>
                ) : items.length === 0 ? (
                    <TableRow>
                        <TableCell columnSpan={WTN_TABLE_COLUMNS.length + 1} alignmentHorizontal="center">
                            <Text color="secondary">
                                <FormattedMessage id={WTN_MSG.NO_RESULTS} defaultMessage="No results found" />
                            </Text>
                        </TableCell>
                    </TableRow>
                ) : (
                    items.map(item => (
                        <TableRow key={item.id}>
                            <TableCell>
                                <Checkbox
                                    checked={selected[item.id] !== undefined}
                                    onChange={() => handleSelectRow(item)}
                                />
                            </TableCell>
                            {WTN_TABLE_COLUMNS.map(col => {
                                const value = col.accessor(item);
                                return (
                                    <TableCell key={col.key}>
                                        <Text>
                                            {col.isDate && value
                                                ? <FormattedDate value={value} year="numeric" month="2-digit" day="2-digit" />
                                                : (value || "—")}
                                        </Text>
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    ))
                )}
            </Table>

            {/* Pagination */}
            <Row alignmentHorizontal="justify" alignmentVertical="center">
                <Select
                    value={pageSize}
                    onChange={(value) => { setPageSize(value); setCurrentPage(0); setSelected({}); }}
                    width={140}
                    size="small"
                >
                    {PAGE_SIZE_OPTIONS.map(size => (
                        <SelectOption key={size} value={size} label={intl.formatMessage({id: WTN_MSG.PER_PAGE, defaultMessage: "{size} per page"}, {size})} />
                    ))}
                </Select>
                <Row spacing="small">
                    <Button
                        type="secondary"
                        size="small"
                        disabled={currentPage === 0 || loading}
                        onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                    >
                        <FormattedMessage id={WTN_MSG.PREVIOUS} defaultMessage="< Previous" />
                    </Button>
                    <Button
                        type="secondary"
                        size="small"
                        disabled={currentPage >= totalPages - 1 || loading}
                        onClick={() => setCurrentPage(prev => prev + 1)}
                    >
                        <FormattedMessage id={WTN_MSG.NEXT} defaultMessage="Next >" />
                    </Button>
                </Row>
            </Row>
            <ContainerView
                open={containerViewOpen}
                onClose={() => setContainerViewOpen(false)}
            />
        </Column>
    );
};

export default injectIntl(WTNInlineView);



// WEBPACK FOOTER //
// ./src/components/wtn/WTNInlineView.js